// src/encounter/lib/to-medication-request.ts
// Build the CreateMedication request body for the pre-encounter IPS Medications section
// (PROD-1257). Shape decoded from a LIVE Flutter network capture of CreateMedication — this
// is the real wire contract, NOT a re-derivation from PatientMedications.toJson(). Key facts:
//   - dose lives in occurenceFrequency[].count (NOT amount — amount is the zeroed {0,""})
//   - encounter is a real encounter Guid (NOT false)
//   - patient = administration.subject; performer = the practitioner id
//   - code.system comes from the medication search result (DDC etc.), not a hardcoded URI
//   - route is a coded {code,display,system}
//   - createdDate is null
// Advanced prescribing (occurrence timing detail, dispense/refills/SIG, dx-link) stays on the
// safe defaults seen in the capture.
//
// The SCHEDULE follows the Flutter rail form (medicine_clinical_info_add_widget.dart and the
// medications case of converter_utils.dart:394-490): `frequency` is the enum id
// (everyday | needed | day_interval | specific), the occurenceFrequency rows come from the shared
// buildOccurrenceRequests (as needed -> [], every day -> one row, days interval -> one row plus
// daysInterval, specific days -> one row per weekday code), administration.startDate is the chosen
// date, and an as-needed medication carries no duration (null, never '').
import {
  MED_FREQUENCY,
  MED_FREQUENCY_ALL,
  buildOccurrenceRequests,
  frequencyIsScheduled,
  parseOccurrenceRequests,
  type MedFrequency,
} from './medication-frequency';
import type { OccurenceRequest } from '@/types/assessment-api';

/** All-zero Guid the capture sends for administration.medication + occurenceFrequency[].medicationId. */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Code.toJson() shape (medication code, route) as it appears on the wire. */
export interface MedCode {
  code: string | null;
  display: string | null;
  system: string | null;
  description: string | null;
  shortName: string | null;
  Definition: string | null;
}

export interface MedAmount {
  value: number;
  unit: string;
}

export interface MedForm {
  system: string | null;
  code: string | null;
  display: string | null;
}

export interface MedOccurrenceFrequency {
  id: string | null;
  /** Units per intake, e.g. "1" or "0.5". */
  count: string | null;
  countMax: string | null;
  /** Every N days (days interval only) — a NUMBER on the wire (Flutter's int `everyNumberOfDay`). */
  daysInterval: number | null;
  /** Times a day. */
  period: number;
  periodMax: string | null;
  /** Weekday code (mo/tu/wd/…) on a specific-days row. */
  periodUnit: string | null;
  occurenceTiming: string | null;
  isActive: boolean | null;
  isDeleted: boolean | null;
  medicationId: string;
}

export interface MedAdministration {
  identifier: string | null;
  medication: string;
  subject: string;
  context: string | null;
  performer: string;
  note: string;
  status: string;
  startDisplayDate: string | null;
  endDisplayDate: string | null;
  /** ISO with no trailing Z, e.g. "2026-06-01T00:00:00.000". */
  startDate: string;
  endDate: string | null;
  isDeleted: boolean;
  performerDetails: string | null;
}

/** CreateMedication body — exact shape from the live capture. */
export interface CreateMedicationBody {
  identifier: string | null;
  orderId: string | null;
  code: MedCode;
  status: string;
  dataAbsentReason: string;
  manufacturer: string;
  form: MedForm;
  amount: MedAmount;
  frequency: string;
  /** null for an as-needed medication (Flutter sends no duration there). */
  duration: string | null;
  durationType: string | null;
  isDeleted: boolean;
  encounter: string;
  isOrder: boolean;
  administration: MedAdministration;
  createdDate: string | null;
  occurenceFrequency: MedOccurrenceFrequency[];
  rxDiagnosisCodesRequest: unknown[];
  route: MedCode;
  unitOfMeasurement: string;
  speciality: string | null;
}

export interface MedicationFormInput {
  /** Selected medication from the search (coded — code/display/system from the result). */
  medication: { code: string; display: string; system: string };
  /** Units per intake → occurenceFrequency[].count, e.g. "1" or "0.5". */
  doseCount?: string;
  /** Frequency enum id (everyday | needed | day_interval | specific). */
  frequency?: string;
  /** Times a day → occurenceFrequency[].period. */
  timesADay?: number;
  /** Every N days (day_interval only) → occurenceFrequency[0].daysInterval. */
  daysInterval?: number;
  /** Weekdays (specific only): full names (Monday…) or wire codes (mo…). One row per day. */
  specificDays?: string[];
  /** Start date, `YYYY-MM-DD` → administration.startDate. */
  startDate?: string;
  /** Duration value, e.g. "60". */
  duration?: string;
  /** Duration unit: day | wk | mo. */
  durationType?: string;
  /** Route of administration (coded). */
  route?: { code: string; display: string; system?: string };
  /** Unit of measurement, e.g. "TABLET". */
  unitOfMeasurement?: string;
  /** Free text on the administration (the chart's Note column), e.g. how the patient says they take it. */
  note?: string;
}

/** Edit-mode prefill (PROD-1493). The medication CODE is fixed in the rail edit — changing the
 *  drug is clinically a new medication (add/stop instead) — so edit mode shows the med as a
 *  static header (no search) and prefills the editable fields from the FULL fetched record. */
export interface MedicationEditPrefill {
  medication: { code: string; display: string; system: string };
  doseCount?: string;
  frequency?: string;
  duration?: string;
  durationType?: string;
  /** FULL route coding from the fetched record — a bare code can't round-trip (an unknown route
   *  must render as its own option and save back verbatim, or the full-replace PUT blanks it —
   *  Codex CONFIRMED, PR #177 review). */
  route?: { code: string; display: string; system?: string };
  unitOfMeasurement?: string;
  /** Times a day (occurenceFrequency[0].period). Absent → the field opens EMPTY, never defaulted. */
  timesADay?: number;
  /** Every N days (occurenceFrequency[0].daysInterval). */
  daysInterval?: number;
  /** Weekday names (Monday…) from the specific-days rows' periodUnit codes. */
  specificDays?: string[];
  /** administration.startDate as `YYYY-MM-DD`. */
  startDate?: string;
}

/** Local midnight ISO with NO trailing Z (the capture sends "...T00:00:00.000"). */
function todayIsoNoZ(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00.000`;
}

function medCode(code: string, display: string, system: string): MedCode {
  return { code, display, system, description: null, shortName: null, Definition: null };
}

function routeCode(route: MedicationFormInput['route']): MedCode {
  return medCode(route?.code ?? '', route?.display ?? '', route?.system ?? '');
}

/** Local-midnight ISO (no Z) for a `YYYY-MM-DD` day string — same format the capture uses. */
function dayToIsoNoZ(yyyymmdd: string): string {
  return `${yyyymmdd}T00:00:00.000`;
}

/** `YYYY-MM-DD` from a wire date ("2026-06-01T00:00:00.000", "2026-06-01T00:00:00Z", …). */
function wireDay(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return m ? m[1] : undefined;
}

/** The frequency as an enum id, or null for a legacy free-text value ("BID") the form can't model. */
export function toMedFrequency(value: string | null | undefined): MedFrequency | null {
  const v = value?.trim();
  return v && MED_FREQUENCY_ALL.some((o) => o.value === v) ? (v as MedFrequency) : null;
}

/** Map the shared OccurenceRequest rows onto the rail's CreateMedication row shape. */
function toRailRows(rows: OccurenceRequest[], count: string | null): MedOccurrenceFrequency[] {
  return rows.map((r) => ({
    id: null,
    count,
    countMax: null,
    daysInterval: r.daysInterval ?? null,
    period: r.period ?? 0,
    periodMax: null,
    periodUnit: r.periodUnit ?? null,
    occurenceTiming: null,
    isActive: r.isActive ?? null,
    isDeleted: r.isDeleted ?? null,
    medicationId: r.medicationId,
  }));
}

/** The schedule rows for an enum frequency (Flutter converter_utils.dart:417-467). */
function scheduleRows(frequency: MedFrequency, input: MedicationFormInput): MedOccurrenceFrequency[] {
  const rows = buildOccurrenceRequests(
    {
      frequency,
      timesADay: input.timesADay,
      daysInterval: input.daysInterval,
      specificDays: input.specificDays,
    },
    EMPTY_GUID,
  );
  return toRailRows(rows, input.doseCount?.trim() || null);
}

/** The legacy single dose row, for a caller that sends no enum frequency. */
function legacyRow(count: string | null): MedOccurrenceFrequency {
  return {
    id: null, count, countMax: null, daysInterval: null, period: 0, periodMax: null,
    periodUnit: null, occurenceTiming: null, isActive: null, isDeleted: null, medicationId: EMPTY_GUID,
  };
}

/** Opaque full medication record (GET GetMedicationDetails) — round-tripped verbatim so the
 *  PUT never blanks server fields we don't model (the PROD-1257 delete lesson). */
export type FullMedicationRecord = Record<string, unknown>;

/**
 * The edit-dialog prefill from the FULL fetched record (GET GetMedicationDetails). Carries the
 * whole schedule — times a day, days interval, specific days, start date — so an edit round-trips
 * it instead of re-asking. A field the record does not hold stays absent: the dialog opens it
 * empty and never invents a value.
 */
export function toMedicationEditPrefill(full: FullMedicationRecord): MedicationEditPrefill {
  const code = (full.code ?? {}) as { code?: string | null; display?: string | null; system?: string | null };
  const route = (full.route ?? {}) as { code?: string | null; display?: string | null; system?: string | null };
  const rows = Array.isArray(full.occurenceFrequency) ? (full.occurenceFrequency as OccurenceRequest[]) : [];
  const live = rows.filter((r) => r && r.isDeleted !== true);
  const frequency = (full.frequency as string | null) ?? undefined;
  const parsed = parseOccurrenceRequests(live, frequency);
  const admin = (full.administration ?? {}) as Record<string, unknown>;
  const startDate = wireDay(admin.startDate);
  const firstCount = live[0]?.count;
  return {
    medication: { code: code.code ?? '', display: code.display ?? '', system: code.system ?? '' },
    doseCount: firstCount != null && firstCount !== '' ? String(firstCount) : undefined,
    frequency,
    duration: (full.duration as string | null) ?? undefined,
    durationType: (full.durationType as string | null) ?? undefined,
    route: route.code
      ? { code: route.code, display: route.display ?? route.code, system: route.system ?? undefined }
      : undefined,
    unitOfMeasurement: (full.unitOfMeasurement as string | null) ?? undefined,
    ...(parsed.timesADay ? { timesADay: parsed.timesADay } : {}),
    ...(parsed.daysInterval ? { daysInterval: parsed.daysInterval } : {}),
    ...(parsed.specificDays ? { specificDays: parsed.specificDays } : {}),
    ...(startDate ? { startDate } : {}),
  };
}

/**
 * EDIT (PROD-1493): merge the dialog's editable fields into the FULL fetched record and return
 * the PUT body. Everything not edited round-trips verbatim. The medication CODE is fixed in the
 * rail edit (changing the drug is clinically a new medication — add/stop instead), so `code` is
 * NOT touched; editable = the schedule, units per intake, unit, route, start date, duration.
 *
 * Schedule rows follow Flutter's edit converter (converter_utils.dart:394-490): each rebuilt row
 * reuses the record's LIVE row at the same position (so its `id` and `medicationId` survive).
 * Rows the new schedule no longer needs go back retired only for Specific days, as Flutter does;
 * the other modes do not send them. Already-deleted rows go back unchanged. A caller that sends no
 * enum frequency keeps the legacy behaviour: only the first live row's count changes.
 */
export function applyMedicationEdit(
  full: FullMedicationRecord,
  input: MedicationFormInput,
): FullMedicationRecord {
  const rows = Array.isArray(full.occurenceFrequency)
    ? (full.occurenceFrequency as Record<string, unknown>[])
    : [];
  const frequency = toMedFrequency(input.frequency);
  const scheduled = frequency ? frequencyIsScheduled(frequency) : true;

  // Positions are aligned against the LIVE rows only — the same rows toMedicationEditPrefill shows.
  // A row the server already holds as deleted is sent back exactly as fetched, never revived by
  // having a rebuilt row land on its id.
  const isDeletedRow = (r: Record<string, unknown>) => r?.isDeleted === true;
  const live = rows.filter((r) => !isDeletedRow(r));
  const alreadyDeleted = rows.filter(isDeletedRow);

  let occurenceFrequency: Record<string, unknown>[];
  if (frequency) {
    const next = scheduleRows(frequency, input);
    const merged = next.map((row, i) => {
      const prev = live[i];
      return prev
        ? { ...prev, ...row, id: prev.id ?? null, medicationId: prev.medicationId ?? row.medicationId }
        : { ...row };
    });
    // Surplus live rows follow Flutter's converter exactly (converter_utils.dart:394-490):
    //   - Specific days (:433-466) sends each surplus row back `isActive:false, isDeleted:true`
    //     (:455-465);
    //   - Every day (:418-424) and Days interval (:425-432) send only the one rebuilt row, and
    //     As needed (:417) sends none — surplus rows are simply not sent.
    const retired =
      frequency === MED_FREQUENCY.specificDays
        ? live.slice(next.length).map((r) => ({ ...r, isActive: false, isDeleted: true }))
        : [];
    occurenceFrequency = [...merged, ...retired, ...alreadyDeleted];
  } else {
    // Legacy (non-enum) frequency: only the first LIVE row's count changes; everything else as is.
    const firstLive = rows.findIndex((r) => !isDeletedRow(r));
    const count = input.doseCount?.trim() || null;
    occurenceFrequency =
      firstLive === -1
        ? [{ ...legacyRow(count) }, ...rows]
        : rows.map((r, i) => (i === firstLive ? { ...r, count } : r));
  }

  const admin = (full.administration ?? {}) as Record<string, unknown>;
  return {
    ...full,
    frequency: input.frequency?.trim() || '',
    duration: scheduled ? input.duration?.trim() || '' : null,
    durationType: scheduled ? input.durationType?.trim() || '' : null,
    unitOfMeasurement: input.unitOfMeasurement?.trim() || '',
    // No route in the input → PRESERVE the record's route verbatim. Writing routeCode(undefined)
    // here blanked a non-Oral route on the full-replace PUT (Codex CONFIRMED, PR #177 review).
    route: input.route ? routeCode(input.route) : full.route,
    ...(input.startDate ? { administration: { ...admin, startDate: dayToIsoNoZ(input.startDate) } } : {}),
    occurenceFrequency,
  };
}

/**
 * STOP / discontinue (PROD-1493): flip the FULL fetched record to the stopped state and return
 * the PUT body. Wire values are Flutter-proven (app_constants medicationStatusStop = "stop";
 * PatientMedications carries status + statusReason; same PUT /medication/{id} the soft-delete
 * uses). The stop date lands on administration.endDate (local-midnight ISO, no Z — the
 * capture's date format); everything else round-trips verbatim. isDeleted stays false — a
 * stopped medication is history, not a tombstone.
 */
export function buildStopMedicationBody(
  full: FullMedicationRecord,
  opts: { endYyyymmdd: string; reason?: string },
): FullMedicationRecord {
  const admin = (full.administration ?? {}) as Record<string, unknown>;
  const reason = opts.reason?.trim();
  return {
    ...full,
    status: 'stop',
    // statusReason only when actually given — the BE currently drops it on the round-trip
    // (live smoke 2026-07-07), and an empty-string key is not the same as an absent key to
    // some BE logging/indexing (CoS review). The UI doesn't capture a reason today.
    ...(reason ? { statusReason: reason } : {}),
    administration: { ...admin, endDate: dayToIsoNoZ(opts.endYyyymmdd) },
  };
}

export function buildCreateMedicationBody(
  input: MedicationFormInput,
  ctx: { patientId: string; encounterId: string; performerId: string; nowIso?: string },
): CreateMedicationBody {
  const frequency = toMedFrequency(input.frequency);
  // As needed (and the hidden `now`) schedules nothing: no rows, no duration.
  const scheduled = frequency ? frequencyIsScheduled(frequency) : true;
  // The chosen start date; today only when none was given. As needed has no date field, and what
  // Flutter sends there is unverified, so the capture's today default is kept for it.
  const startDate = input.startDate ? dayToIsoNoZ(input.startDate) : ctx.nowIso ?? todayIsoNoZ();
  return {
    identifier: null,
    orderId: null,
    code: medCode(input.medication.code, input.medication.display, input.medication.system),
    status: 'Active',
    dataAbsentReason: 'n/a',
    manufacturer: 'n/a',
    form: { system: null, code: null, display: null },
    amount: { value: 0, unit: '' },
    frequency: input.frequency?.trim() || '',
    duration: scheduled ? input.duration?.trim() || '' : null,
    durationType: scheduled ? input.durationType?.trim() || '' : null,
    isDeleted: false,
    encounter: ctx.encounterId,
    isOrder: false,
    administration: {
      identifier: null,
      medication: EMPTY_GUID,
      subject: ctx.patientId,
      context: null,
      performer: ctx.performerId,
      note: input.note?.trim() || '',
      status: 'Active',
      startDisplayDate: null,
      endDisplayDate: null,
      startDate,
      endDate: null,
      isDeleted: false,
      performerDetails: null,
    },
    createdDate: null,
    occurenceFrequency: frequency
      ? scheduleRows(frequency, input)
      : [legacyRow(input.doseCount?.trim() || null)],
    rxDiagnosisCodesRequest: [],
    route: routeCode(input.route),
    unitOfMeasurement: input.unitOfMeasurement?.trim() || '',
    speciality: null,
  };
}
